import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface DonutChartProps {
  title: string;
  labels?: string[];
  series?: number[];
  colors?: string[];
  selectOptions?: { value: string; label: string }[];
  selected?: string;
  onSelectChange?: (value: string) => void;
}

// ✅ Componente interno para el gráfico y leyendas
const DonutChartContent: React.FC<{
  labels: string[];
  series: number[];
  colors: string[];
}> = ({ labels, series, colors }) => {
  const options: ApexOptions = {
    chart: { fontFamily: 'Satoshi, sans-serif', type: 'donut' },
    colors,
    labels,
    legend: { show: false },
    plotOptions: {
      pie: { donut: { size: '65%', background: 'transparent' } },
    },
    dataLabels: { enabled: false },
    responsive: [
      { breakpoint: 2600, options: { chart: { width: 380 } } },
      { breakpoint: 640, options: { chart: { width: 200 } } },
    ],
  };

  return (
    <>
      <div className="mb-4">
        <div
          className="mx-auto flex justify-center"
          style={{ minHeight: '220px' }}
        >
          <ReactApexChart options={options} series={series} type="donut" />
        </div>
      </div>

      <div className="flex flex-col gap-y-3 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-6">
        {labels.map((label, idx) => (
          <div
            key={label}
            className="flex items-center justify-between gap-2 sm:w-1/2 lg:w-auto"
          >
            <span
              className="block h-3 w-3 flex-shrink-0 rounded-full"
              style={{ backgroundColor: colors[idx] }}
            ></span>
            <p className="flex w-full justify-between text-sm font-medium text-black dark:text-white">
              <span className="mr-2">{label}</span>
              <span>{series[idx]}%</span>
            </p>
          </div>
        ))}
      </div>
    </>
  );
};

const DonutChart: React.FC<DonutChartProps> = ({
  title,
  labels = [],
  series = [],
  colors = ['#CBD5E1'], // color gris por defecto
  selectOptions,
  selected,
  onSelectChange,
}) => {
  // ✅ Si no hay datos, usamos fallback
  const hasData = labels.length > 0 && series.length > 0;
  const safeLabels = hasData ? labels : ['Sin datos'];
  const safeSeries = hasData ? series : [0];
  const safeColors = hasData ? colors : ['#CBD5E1'];

  return (
    <div className="col-span-12 rounded-sm border border-stroke bg-white px-4 py-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-6 lg:px-7.5">
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h5 className="text-lg font-semibold text-black dark:text-white sm:text-xl">
          {title}
        </h5>

        {selectOptions && selectOptions.length > 0 && (
          <div className="relative inline-block w-full sm:w-auto">
            <select
              value={selected}
              onChange={(e) => onSelectChange?.(e.target.value)}
              className="w-full appearance-none rounded border border-stroke bg-transparent py-2 pl-3 pr-8 text-sm font-medium outline-none dark:border-strokedark dark:bg-boxdark sm:w-auto"
            >
              {selectOptions.map((opt) => (
                <option key={opt.value} value={opt.value} className="dark:bg-boxdark">
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              <svg
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.47 1.09C0.47 1.03 0.50 0.95 0.54 0.91C0.65 0.81 0.81 0.81 0.91 0.90L4.85 4.60C4.93 4.68 5.06 4.68 5.15 4.60L9.09 0.90C9.19 0.79 9.36 0.81 9.46 0.91C9.56 1.01 9.55 1.17 9.44 1.28L5.50 4.99C5.22 5.24 4.78 5.24 4.51 4.99L0.56 1.28C0.50 1.22 0.47 1.16 0.47 1.09Z"
                  fill="#637381"
                />
              </svg>
            </span>
          </div>
        )}
      </div>

      {/* Contenido dinámico */}
      <DonutChartContent
        labels={safeLabels}
        series={safeSeries}
        colors={safeColors}
      />
    </div>
  );
};

export default DonutChart;
